package com.example.mystybag.appCore.views.pages

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.platform.LocalSoftwareKeyboardController
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.KeyboardType
import com.arkivanov.decompose.extensions.compose.subscribeAsState
import com.example.mystybag.appCore.logic.pages.CompanyResponsiblePersonEditViewModel
import com.example.mystybag.appCore.views.components.ClientWebHeaderBar
import com.example.mystybag.appCore.views.components.WebHeaderBottomDivider
import com.example.mystybag.common.resources.SR
import com.example.mystybag.common.extensions.scrollEdgeFade
import com.example.mystybag.common.ui.components.custom.DefaultCircleShapedTextField
import com.example.mystybag.common.ui.components.native.CaptionCapitalization
import com.example.mystybag.common.ui.components.native.DefaultButton
import com.example.mystybag.common.ui.components.native.Texts
import com.example.mystybag.common.ui.views.UIView
import com.example.mystybag.core.expected.rememberIsWebDesktopLayout
import com.example.mystybag.core.expected.sharedString
import com.example.mystybag.core.resources.SRC
import com.example.mystybag.core.ui.theme.LocalTheme.p8
import com.example.mystybag.core.ui.theme.LocalTheme.p10
import com.example.mystybag.core.ui.theme.LocalTheme.p12
import com.example.mystybag.core.ui.theme.LocalTheme.p16
import com.example.mystybag.core.ui.theme.LocalTheme.p18
import com.example.mystybag.core.ui.theme.LocalTheme.p20
import com.example.mystybag.core.ui.theme.LocalTheme.p24
import com.example.mystybag.core.ui.theme.LocalTheme.p48
import com.example.mystybag.core.ui.theme.LocalTheme.p56
import com.example.mystybag.core.views.AppTheme
import com.example.mystybag.common.views.CountryCodePickerBottomSheet
import dev.icerock.moko.resources.compose.painterResource
import dev.icerock.moko.resources.compose.stringResource
import androidx.compose.ui.unit.dp

@Composable
fun CompanyResponsiblePersonEditView(viewModel: CompanyResponsiblePersonEditViewModel) {
    val keyboardController = LocalSoftwareKeyboardController.current

    if (rememberIsWebDesktopLayout()) {
        CompanyResponsiblePersonEditWebView(viewModel)
    } else {
    UIView(
        viewModel = viewModel,
        navigationContent = {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(start = p16, end = p16, top = p12, bottom = p16)
            ) {
                Texts.TitleMedium(
                    text = sharedString(SR.strings.responsible_person_details),
                    color = AppTheme.current.colors.onSurface,
                    modifier = Modifier
                        .fillMaxWidth()
                        .align(Alignment.Center),
                    textAlign = androidx.compose.ui.text.style.TextAlign.Center
                )
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .align(Alignment.CenterStart),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(p16)
                ) {
                    Box(
                        modifier = Modifier
                            .size(p48)
                            .clip(CircleShape)
                            .background(AppTheme.current.colors.surfaceContainerLowest)
                            .clickable {
                                keyboardController?.hide()
                                viewModel.navCallbacks.backButtonCallback()
                            },
                        contentAlignment = Alignment.Center
                    ) {
                        androidx.compose.foundation.Image(
                            painter = painterResource(SRC.images.arrow_back),
                            contentDescription = null,
                            contentScale = ContentScale.Fit,
                            modifier = Modifier.size(width = p10, height = p18),
                            colorFilter = androidx.compose.ui.graphics.ColorFilter.tint(AppTheme.current.colors.primary)
                        )
                    }
                }
            }
        }
    ) { state ->
        val uiState by state

            CompanyResponsiblePersonEditContent(
                uiState = uiState,
                viewModel = viewModel,
                modifier = Modifier.fillMaxSize()
            )
    }
    }

    val uiStateForSheet by viewModel.uiState.subscribeAsState()
    CountryCodePickerBottomSheet(
        isVisible = uiStateForSheet.showDialCodePicker,
        countries = uiStateForSheet.countries,
        selectedDialCode = uiStateForSheet.dialCode,
        onCountrySelected = { country ->
            viewModel.selectDialCodeCountry(country.dialCode)
        },
        onDismiss = viewModel::hideDialCodePicker
    )
}

@Composable
private fun CompanyResponsiblePersonEditWebView(viewModel: CompanyResponsiblePersonEditViewModel) {
    val uiState by viewModel.uiState.subscribeAsState()

    UIView(
        modifier = Modifier.fillMaxSize(),
        viewModel = viewModel,
        showTopBar = false,
        removeVerticalPadding = true
    ) {
        Column(modifier = Modifier.fillMaxSize()) {
            ClientWebHeaderBar(
                selectedTab = 3,
                homeTabIndex = 0,
                deliveryTabIndex = 1,
                favoritesTabIndex = 2,
                menuTabIndex = 3,
                searchCallbacks = null,
                onFilterClick = {},
                showSearchAndFilter = false,
                showBackButton = true,
                onBackClick = { viewModel.navCallbacks.backButtonCallback() }
            )
            WebHeaderBottomDivider()
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .weight(1f),
                contentAlignment = Alignment.TopCenter
            ) {
                CompanyResponsiblePersonEditContent(
                    uiState = uiState,
                    viewModel = viewModel,
                    modifier = Modifier
                        .width(1080.dp)
                        .fillMaxHeight()
                        .padding(horizontal = p16, vertical = p8)
                )
            }
        }
    }
}

@Composable
private fun CompanyResponsiblePersonEditContent(
    uiState: com.example.mystybag.appCore.model.pages.CompanyResponsiblePersonEditUIState,
    viewModel: CompanyResponsiblePersonEditViewModel,
    modifier: Modifier
) {
    val isWebDesktop = rememberIsWebDesktopLayout()
    if (uiState.isInitialLoad) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(p16),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.Center
        ) {
            Spacer(modifier = Modifier.height(p20))
            CircularProgressIndicator()
        }
    } else {
        Column(
            modifier = modifier
        ) {
            Column(
                modifier = Modifier
                    .weight(1f)
                    .fillMaxWidth()
                    .verticalScroll(rememberScrollState())
                    .scrollEdgeFade(showTop = false)
                    .padding(horizontal = p16),
                verticalArrangement = Arrangement.spacedBy(p24)
            ) {
                DefaultCircleShapedTextField(
                    modifier = Modifier.fillMaxWidth(),
                    value = uiState.firstName,
                    label = stringResource(SR.strings.first_name_label),
                    errorText = uiState.firstNameErrorMsg,
                    onValueChange = viewModel::onFirstNameChange,
                    isError = uiState.firstNameIsError,
                    keyboardOptions = KeyboardOptions(imeAction = ImeAction.Next)
                )

                DefaultCircleShapedTextField(
                    modifier = Modifier.fillMaxWidth(),
                    value = uiState.lastName,
                    label = stringResource(SR.strings.last_name_label),
                    errorText = uiState.lastNameErrorMsg,
                    onValueChange = viewModel::onLastNameChange,
                    isError = uiState.lastNameIsError,
                    keyboardOptions = KeyboardOptions(imeAction = ImeAction.Next)
                )

                Column(
                    modifier = Modifier.fillMaxWidth(),
                    verticalArrangement = Arrangement.spacedBy(p12)
                ) {
                    Texts.BodyMedium(
                        text = stringResource(SR.strings.contact_number),
                        modifier = Modifier.fillMaxWidth()
                    )
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(p8)
                    ) {
                        Box(modifier = Modifier.weight(0.32f)) {
                            DefaultCircleShapedTextField(
                                value = uiState.dialCode,
                                onValueChange = { },
                                label = stringResource(SR.strings.dial_code),
                                modifier = Modifier.fillMaxWidth(),
                                readOnly = true,
                                enabled = true,
                                canFocus = false,
                                errorText = uiState.dialCodeErrorMsg,
                                isError = uiState.dialCodeIsError,
                                backgroundColor = AppTheme.current.colors.surface,
                                borderColor = AppTheme.current.colors.outline,
                                trailingIcon = {
                                    androidx.compose.foundation.Image(
                                        painter = painterResource(
                                            when {
                                                uiState.showDialCodePicker -> SRC.images.combobox_arrow_down_green
                                                uiState.dialCode.isNotEmpty() -> SRC.images.combobox_arrow_right_green
                                                else -> SRC.images.combobox_arrow_right_gray
                                            }
                                        ),
                                        modifier = Modifier.size(p16),
                                        contentDescription = null
                                    )
                                }
                            )
                            Box(
                                modifier = Modifier
                                    .matchParentSize()
                                    .clickable { viewModel.showDialCodePicker() }
                            )
                        }
                        DefaultCircleShapedTextField(
                            value = uiState.phoneNumber,
                            onValueChange = viewModel::onPhoneNumberChange,
                            label = stringResource(SR.strings.phone_number_label),
                            modifier = Modifier.weight(0.68f),
                            errorText = uiState.phoneNumberErrorMsg,
                            isError = uiState.phoneNumberIsError,
                            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Phone, imeAction = ImeAction.Done)
                        )
                    }
                }
            }

            DefaultButton(
                modifier = Modifier
                    .fillMaxWidth(if (isWebDesktop) 0.5f else 1f)
                    .align(Alignment.CenterHorizontally)
                    .height(p56)
                    .padding(horizontal = if (isWebDesktop) 0.dp else p16)
                    .padding(bottom = if (isWebDesktop) 32.dp else p8),
                caption = sharedString(SR.strings.save),
                capitalization = CaptionCapitalization.None,
                contentPadding = if (isWebDesktop) PaddingValues(horizontal = p16, vertical = 0.dp) else null,
                callback = { viewModel.onSubmit() }
            )
        }
    }
}

